import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap'
import useFetch from '../../_fetch'
import './user-settings.css'

type AccessMember = {
    email: string
    role: 'manager' | 'member'
    status: 'pending' | 'active'
    invitedAt?: string
    acceptedAt?: string
}

type AccountAccess = {
    emailOnly: boolean
    members: AccessMember[]
}

type AccountAccessCardProps = {
    basePath?: string
    accountName?: string
    onChanged?: () => void
}

export default function AccountAccessCard({ basePath = '/user/access', accountName, onChanged }: AccountAccessCardProps = {}) {
    const { doFetch } = useFetch()
    const [access, setAccess] = useState<AccountAccess | null>(null)
    const [email, setEmail] = useState('')
    const [role, setRole] = useState<'manager' | 'member'>('member')
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState('')
    const [message, setMessage] = useState<{ variant: 'success' | 'danger', text: string } | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await doFetch(basePath)
            if (!response.ok) throw new Error(await response.text())
            setAccess(await response.json())
        } catch (error) {
            setMessage({ variant: 'danger', text: error instanceof Error ? error.message : 'Could not load account access.' })
        } finally {
            setLoading(false)
        }
    }, [basePath, doFetch])

    useEffect(() => { load() }, [load])

    const run = async (key: string, action: () => Promise<Response>, success: string | ((body: any) => string)) => {
        setBusy(key)
        setMessage(null)
        try {
            const response = await action()
            if (!response.ok) throw new Error(await response.text())
            const body = await response.json()
            if (body?.members) setAccess(body)
            else await load()
            setMessage({ variant: 'success', text: typeof success === 'function' ? success(body) : success })
            onChanged?.()
            return true
        } catch (error) {
            await load()
            setMessage({ variant: 'danger', text: error instanceof Error ? error.message : 'The action failed.' })
            return false
        } finally {
            setBusy('')
        }
    }

    const invite = async (event: React.FormEvent) => {
        event.preventDefault()
        const sent = await run(
            'invite',
            () => doFetch(`${basePath}/invitations`, { email, role }),
            (body) => body?.provisioning === 'direct'
                ? 'Access granted. The person can switch to this account immediately.'
                : 'Invitation sent to the new user.'
        )
        if (sent) setEmail('')
    }

    const updatePolicy = async (emailOnly: boolean) => {
        if (emailOnly && !window.confirm('Only e-mail login will immediately sign out username-based sessions and block existing account switching into this account. Continue?')) return
        await run('policy', () => doFetch(`${basePath}/login-policy`, { emailOnly }, 'PATCH'), `E-mail-only login ${emailOnly ? 'enabled' : 'disabled'}.`)
    }

    const activeMembers = access?.members.filter((member) => member.status === 'active').length || 0
    const pendingMembers = access?.members.filter((member) => member.status === 'pending').length || 0

    return (
        <Card className="user-settings-panel border-0" data-testid="account-access-card">
            <Card.Header className="bg-white border-0 user-settings-card-header d-flex flex-column flex-sm-row justify-content-between gap-3">
                <div>
                    <div className="user-settings-eyebrow">Access management</div>
                    <h5 className="mb-1">People with account access</h5>
                    <div className="text-muted small">Invite colleagues and decide who can manage {accountName || 'this account'}.</div>
                </div>
                {access && (
                    <div className="d-flex align-items-start gap-2">
                        <Badge bg="light" text="dark" className="user-settings-count-badge">{activeMembers} active</Badge>
                        {!!pendingMembers && <Badge bg="warning" text="dark" className="user-settings-count-badge">{pendingMembers} pending</Badge>}
                    </div>
                )}
            </Card.Header>
            <Card.Body>
                {message && <Alert variant={message.variant} className="user-settings-alert" aria-live="polite">{message.text}</Alert>}
                {loading || !access ? (
                    <div className="user-settings-loading-state">
                        <Spinner animation="border" size="sm" />
                        <span>Loading account access…</span>
                    </div>
                ) : <>
                    <div className="user-settings-section-heading">
                        <h6>Add a person</h6>
                        <p>Existing users get access immediately without an e-mail. New users receive one invitation.</p>
                    </div>
                    <Form onSubmit={invite} className="user-settings-invite-panel">
                        <Row className="g-3 align-items-end">
                            <Col md={6}>
                                <Form.Label htmlFor="account-access-email">E-mail address</Form.Label>
                                <Form.Control id="account-access-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="person@company.com" />
                            </Col>
                            <Col md={3}>
                                <Form.Label htmlFor="account-access-role">Role</Form.Label>
                                <Form.Select id="account-access-role" value={role} onChange={(event) => setRole(event.target.value as 'manager' | 'member')}>
                                    <option value="member">Member</option>
                                    <option value="manager">Manager</option>
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Button type="submit" className="w-100" disabled={!!busy}>{busy === 'invite' ? 'Adding…' : 'Add access'}</Button>
                            </Col>
                        </Row>
                    </Form>

                    <div className="user-settings-section-heading mt-4">
                        <h6>Members</h6>
                        <p>Role and access changes take effect on the next request.</p>
                    </div>
                    <div className="user-settings-table-wrap">
                        <Table responsive hover className="align-middle mb-0">
                            <thead><tr><th>E-mail</th><th>Status</th><th>Role</th><th className="text-end">Actions</th></tr></thead>
                            <tbody>
                                {access.members.length ? access.members.map((member) => <tr key={member.email}>
                                    <td className="fw-medium">{member.email}</td>
                                    <td><Badge bg={member.status === 'active' ? 'success' : 'warning'} text={member.status === 'pending' ? 'dark' : undefined} className="text-capitalize">{member.status}</Badge></td>
                                    <td className="user-settings-role-cell">
                                        <Form.Select size="sm" aria-label={`Role for ${member.email}`} value={member.role} disabled={!!busy}
                                            onChange={(event) => run(`role:${member.email}`, () => doFetch(`${basePath}/members/${encodeURIComponent(member.email)}`, { role: event.target.value }, 'PATCH'), 'Role updated.')}>
                                            <option value="member">Member</option>
                                            <option value="manager">Manager</option>
                                        </Form.Select>
                                    </td>
                                    <td className="text-end text-nowrap">
                                        {member.status === 'pending' && <Button size="sm" variant="outline-secondary" className="me-2" disabled={!!busy}
                                            onClick={() => run(`resend:${member.email}`, () => doFetch(`${basePath}/invitations/resend`, { email: member.email }), 'Invitation resent.')}>Resend</Button>}
                                        <Button size="sm" variant="outline-danger" disabled={!!busy}
                                            onClick={() => window.confirm(`Remove ${member.email} from this account?`) && run(`remove:${member.email}`, () => doFetch(`${basePath}/members/${encodeURIComponent(member.email)}`, undefined, 'DELETE'), 'Access removed.')}>Remove</Button>
                                    </td>
                                </tr>) : <tr><td colSpan={4} className="user-settings-empty-row">No e-mail access has been configured yet.</td></tr>}
                            </tbody>
                        </Table>
                    </div>

                    <div className="user-settings-section-divider" />
                    <div className="user-settings-policy-panel">
                        <div>
                            <div className="fw-semibold">Only allow e-mail login</div>
                            <div className="text-muted small mt-1">Requires an active, verified e-mail manager.</div>
                        </div>
                        <Form.Check
                            type="switch"
                            id="email-only-login"
                            aria-label="Only allow e-mail login"
                            checked={access.emailOnly}
                            disabled={!!busy}
                            onChange={(event) => updatePolicy(event.target.checked)}
                        />
                    </div>
                </>}
            </Card.Body>
        </Card>
    )
}
